import React, { Fragment, type PropsWithChildren } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { TriggerPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import { ExpandButtonWithCreateChildButton } from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIsChildIssueCreateEnabled } from '../../../../controllers/features/selectors.tsx';
import { useRowContext } from '../../../rows/row-context/index.tsx';
import type { FirstColumnCell$key } from './__generated__/FirstColumnCell.graphql';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="first-column-cell" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

export type Props = {
	isRowHoveredOrSelected: boolean;
	depth: number;
	itemsConnectionId: string;
	issueRef: FirstColumnCell$key;
	triggerProps?: TriggerPropsType;
	isFormVisible?: boolean;
};

/**
 * @deprecated should be removed when jira_list_merged_column_gate is cleaned up
 */
const FirstColumnCell = ({
	isRowHoveredOrSelected,
	itemsConnectionId,
	depth,
	issueRef,
	triggerProps,
	isFormVisible,
}: Props) => {
	const issueData = useFragment<FirstColumnCell$key>(
		graphql`
			fragment FirstColumnCell on JiraIssueEdge
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
			) {
				hasChildren(filterByProjectKeys: $projectKeys)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				canHaveChildIssues(projectKey: $projectKey)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				node {
					issueId
				}
			}
		`,
		issueRef,
	);

	const { hasChildren, canHaveChildIssues } = issueData;
	const { issueId } = issueData.node ?? {};
	const { groupId } = useRowContext();

	const isChildIssueCreateEnabled = useIsChildIssueCreateEnabled();
	const showCreateChildButton =
		isChildIssueCreateEnabled && isRowHoveredOrSelected && Boolean(canHaveChildIssues);

	return (
		issueId && (
			<MaybeUFOSegment>
				<ExpandButtonWithCreateChildButton
					itemId={issueId}
					itemsConnectionId={itemsConnectionId}
					depth={depth}
					hasChildren={Boolean(hasChildren)}
					showCreateChildButton={showCreateChildButton}
					isRowHoveredOrSelected={isRowHoveredOrSelected}
					isCreateChildFormVisible={isFormVisible}
					createChildTriggerProps={triggerProps}
					groupId={groupId}
				/>
			</MaybeUFOSegment>
		)
	);
};

export default FirstColumnCell;
